import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ClientAvatar from "../ClientAvatar";
import PackageCubeIcon from "../icons/PackageCubeIcon";
import { PHOTO_UPDATED_EVENT } from "../ProfilePhotoPicker";
import { PROFILE_UPDATED_EVENT } from "../profileEvents";
import { useNotifications } from "../../../../context/NotificationsContext";
import { appLogo } from "../../../../../brands";
import "./AppShell.css";

// Shared by the Mobile drawer, Mobile bottom nav, AND the Desktop/Tablet sidebar. Termini lives
// under Settings now (Screens/secure/Settings → Termini), not as its own top-level item — see
// Appointments/index.js. Reports (Screens/secure/Reports, currently only on the reports-v1
// branch) slots back in here as one more entry once merged, same as every item below.
// Istorija paketa (PackageHistory) is being finished on the dev-package-history branch — its
// Settings entry point/route are removed here on dev until that's ready to ship.
const NAV_ITEMS = [
  { key: "calendar", label: "Kalendar", path: "/", icon: CalendarMonthIcon },
  { key: "members", label: "Članovi", path: "/members", icon: GroupsIcon },
  {
    key: "packages",
    label: "Upravljanje paketima",
    path: "/packages",
    icon: PackageCubeIcon,
  },
  {
    key: "settings",
    label: "Podešavanja",
    path: "/settings",
    icon: SettingsIcon,
  },
];

// Mobile-only — the trainer's own Profile (Screens/secure/Profile) is reached via this
// dedicated icon on Mobile, appended after NAV_ITEMS in the drawer/bottom-nav only (never in
// the Desktop/Tablet sidebar, which instead makes its own trainer footer below clickable).
const MOBILE_ONLY_ITEMS = [
  { key: "profile", label: "Profil", path: "/profile", icon: AccountCircleIcon },
];

// The Mobile bottom nav only has room for 4 icons and doesn't need (or want) to mirror
// NAV_ITEMS' own order 1:1 — Podešavanja stays reachable from the drawer only (still listed
// there via [...NAV_ITEMS, ...MOBILE_ONLY_ITEMS] below, "everything" as ever), while Upravljanje
// paketima earns the spot instead as a more frequent trainer task. Its own explicit list/order,
// rather than a `hideFromBottomNav` flag on NAV_ITEMS, since the two bars can now disagree on
// both membership AND order without one flag trying to encode both.
const BOTTOM_NAV_ORDER = ["calendar", "packages", "members", "profile"];

// Desktop/tablet sidebar only — its own icon set override so the
// Mobile drawer and bottom nav (which share NAV_ITEMS) are untouched.
const SIDEBAR_ICONS = {
  calendar: CalendarMonthOutlinedIcon,
  members: GroupsOutlinedIcon,
  packages: PackageCubeIcon,
  settings: SettingsOutlinedIcon,
};

const AppShell = ({ active, children }) => {
  const navigate = useNavigate();
  // Backdrop/drawer are now ALWAYS rendered (see the JSX below — no more `{drawerOpen && ...}`
  // conditional mount), same pattern web_user_v2's own NavDrawer already used correctly; only
  // the `.open` class toggles. Mounting/unmounting on every open — the previous approach —
  // needed a `requestAnimationFrame` dance to get the OPEN direction to animate at all (a
  // transition needs an already-painted starting value to animate from), and reopening before
  // a still-in-flight close's unmount timer had fully cleared left room for exactly the kind
  // of "fine the first time, broken the second" state race that turned up (top safe-area
  // inset misapplied on a quick second open). Keeping one element in the DOM permanently and
  // just flipping a class sidesteps that whole category of bug — its layout (including
  // `env(safe-area-inset-top)`) gets computed once, correctly, and never has to be redone.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const touchStartX = useRef(null);
  const { unreadCount, openNotif } = useNotifications();

  // Both name and photo need to be real state (not plain consts re-read from localStorage on
  // every render) so a save on the Profile screen can update them here without a page
  // refresh — nothing else forces AppShell to re-render when localStorage changes underneath
  // it, so each needs its own custom-event listener the same way `photoUrl` already does.
  const [firstName, setFirstName] = useState(
    () => localStorage.getItem("firstName") || ""
  );
  const [lastName, setLastName] = useState(
    () => localStorage.getItem("lastName") || ""
  );
  const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();

  const [photoUrl, setPhotoUrl] = useState(
    () => localStorage.getItem("photoUrl") || null
  );

  useEffect(() => {
    const onPhotoUpdated = (e) => setPhotoUrl(e.detail.photoUrl);
    window.addEventListener(PHOTO_UPDATED_EVENT, onPhotoUpdated);
    return () =>
      window.removeEventListener(PHOTO_UPDATED_EVENT, onPhotoUpdated);
  }, []);

  useEffect(() => {
    const onProfileUpdated = (e) => {
      setFirstName(e.detail.firstName);
      setLastName(e.detail.lastName);
    };
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () =>
      window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    window.location.replace("/login");
  };

  const go = (path) => {
    setDrawerOpen(false);
    navigate(path);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx < -40) setDrawerOpen(false);
    touchStartX.current = null;
  };

  return (
    <div className="hf-shell">
      <button
        type="button"
        className="hf-shell-hamburger"
        aria-label="Meni"
        onClick={() => setDrawerOpen(true)}
      >
        <MenuIcon />
      </button>

      <button
        type="button"
        className={`hf-shell-bell${active === "calendar" ? " hf-shell-bell-hidden-desktop" : ""}`}
        aria-label="Obaveštenja"
        onClick={openNotif}
      >
        <NotificationsOutlinedIcon />
        {unreadCount > 0 && (
          <span className="hf-shell-bell-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Always mounted (never conditionally rendered) — see the drawerOpen comment above for
          why; `.open` is the only thing that toggles, and `pointer-events` (AppShell.css)
          keeps the closed state from blocking clicks/touches on the page underneath it. */}
      <div
        className={`hf-shell-backdrop${drawerOpen ? " open" : ""}`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden={!drawerOpen}
      />
      <div
        className={`hf-shell-drawer${drawerOpen ? " open" : ""}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="hf-shell-drawer-header">
          <div className="hf-shell-drawer-logo">
            <img src="/img/hitfitlogo.jpg" alt="HitFit" />
            <span className="hf-shell-drawer-appname">Office App</span>
          </div>
          <button
            type="button"
            className="hf-shell-close"
            aria-label="Zatvori meni"
            onClick={() => setDrawerOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {(firstName || lastName) && (
          <div className="hf-shell-drawer-profile">
            <ClientAvatar
              photoUrl={photoUrl}
              initials={initials}
              wrapperClassName="hf-shell-avatar"
              imgClassName="hf-shell-avatar-img"
            />
            <div className="hf-shell-drawer-name">
              {firstName} {lastName}
            </div>
          </div>
        )}

        <ul className="hf-shell-drawer-menu">
          {[...NAV_ITEMS, ...MOBILE_ONLY_ITEMS].map(({ key, label, path, icon: Icon }) => (
            <li
              key={key}
              className={`hf-shell-drawer-item${active === key ? " active" : ""}`}
              onClick={() => go(path)}
            >
              <Icon fontSize="small" />
              {label}
            </li>
          ))}
        </ul>

        {/* Fixed at the bottom, separated by its own top border — outside the menu's own
            scroll area (.hf-shell-drawer-menu) so it stays put instead of scrolling away
            with the rest of the list. */}
        <div className="hf-shell-drawer-footer">
          <button
            type="button"
            className="hf-shell-drawer-item hf-shell-danger"
            onClick={handleLogout}
          >
            <LogoutIcon fontSize="small" />
            Odjavi se
          </button>
        </div>
      </div>

      <nav className="hf-shell-bottomnav">
        {BOTTOM_NAV_ORDER.map((key) => {
          const item = [...NAV_ITEMS, ...MOBILE_ONLY_ITEMS].find((i) => i.key === key);
          if (!item) return null;
          const { label, path, icon: Icon } = item;
          return (
            <button
              key={key}
              type="button"
              aria-label={label}
              className={`hf-shell-navitem${active === key ? " active" : ""}`}
              onClick={() => go(path)}
            >
              <Icon />
            </button>
          );
        })}
      </nav>

      <nav className="hf-shell-sidebar">
        <div className="hf-shell-sidebar-logo">
          <div className="hf-shell-sidebar-badge">
            <img src={appLogo} alt="Logo" className="hf-shell-sidebar-mark" />
          </div>
        </div>

        <ul className="hf-shell-sidebar-menu">
          {NAV_ITEMS.map(({ key, label, path }) => {
            const Icon = SIDEBAR_ICONS[key];
            return (
              <li key={key}>
                <button
                  type="button"
                  aria-label={label}
                  className={`hf-shell-sidebar-item${active === key ? " active" : ""}`}
                  onClick={() => go(path)}
                >
                  <Icon />
                  <span className="hf-shell-sidebar-label">{label}</span>
                </button>
              </li>
            );
          })}
        </ul>

        {/* Desktop/Tablet's Profile entry point — no separate "Profile" nav item (removed per
            spec); clicking the already-existing trainer footer opens it instead. */}
        <button
          type="button"
          className="hf-shell-sidebar-footer"
          onClick={() => go("/profile")}
        >
          <ClientAvatar
            photoUrl={photoUrl}
            initials={initials}
            wrapperClassName="hf-shell-avatar"
            imgClassName="hf-shell-avatar-img"
          />
          <div className="hf-shell-footer-text">
            <div className="hf-shell-footer-name">
              {firstName} {lastName}
            </div>
            <div className="hf-shell-footer-role">Trener</div>
          </div>
        </button>
      </nav>

      <main className="hf-shell-content">{children}</main>
    </div>
  );
};

export default AppShell;
